<template>
  <div class="custom-date-range-wrapper">
    <div v-if="label && !twoFields" class="custom-date-range__label">
      {{ label }}<span v-if="required" style="color: red;">*</span>
    </div>

    <!-- Режим двух полей: два отдельных календаря, вся зона кликабельна, можно ввести дату с клавиатуры -->
    <div v-if="twoFields" class="custom-date-range-two">
      <div class="custom-date-range-two__field">
        <label class="custom-date-range-two__label">Дата начала</label>
        <div class="custom-date-range-two__input-wrap" @click="onInputWrapClick">
          <q-input
            :model-value="fromText"
            placeholder="дд.мм.гггг"
            :disable="disabled"
            outlined
            dense
            class="custom-date-range-two__input"
            @update:model-value="fromText = formatDateInputWithDots($event)"
            @blur="commitFromText"
            @keydown.enter="commitFromText"
          >
            <template v-slot:append>
              <div class="custom-date-range-two__icon-btn" @click.stop="openFromPopup">
                <q-icon name="event" size="20px" color="grey-7" />
              </div>
              <q-icon v-if="value?.from" name="cancel" class="cursor-pointer custom-date-range-two__icon" size="18px" @click.stop.prevent="clearValue" />
            </template>
          </q-input>
        </div>
        <q-popup-proxy ref="popupFromRef" v-model="popupFromOpen" transition-show="scale" transition-hide="scale">
          <q-date
            v-model="tempFrom"
            :options="optionsFn"
            mask="YYYY-MM-DD"
            :locale="dateLocaleRu"
            minimal
            class="custom-date-range-two__calendar"
            @update:model-value="onFromPicked"
          />
        </q-popup-proxy>
      </div>
      <div class="custom-date-range-two__field">
        <label class="custom-date-range-two__label">Дата окончания</label>
        <div class="custom-date-range-two__input-wrap" @click="onInputWrapClick">
          <q-input
            :model-value="toText"
            placeholder="дд.мм.гггг"
            :disable="disabled"
            outlined
            dense
            class="custom-date-range-two__input"
            @update:model-value="toText = formatDateInputWithDots($event)"
            @blur="commitToText"
            @keydown.enter="commitToText"
          >
            <template v-slot:append>
              <div class="custom-date-range-two__icon-btn" @click.stop="openToPopup">
                <q-icon name="event" size="20px" color="grey-7" />
              </div>
              <q-icon v-if="value?.to" name="cancel" class="cursor-pointer custom-date-range-two__icon" size="18px" @click.stop.prevent="clearTo" />
            </template>
          </q-input>
        </div>
        <q-popup-proxy ref="popupToRef" v-model="popupToOpen" transition-show="scale" transition-hide="scale">
          <q-date
            v-model="tempTo"
            :default-year-month="defaultYearMonthTo"
            :options="optionsFnTo"
            mask="YYYY-MM-DD"
            :locale="dateLocaleRu"
            minimal
            class="custom-date-range-two__calendar"
            @update:model-value="onToPicked"
          />
        </q-popup-proxy>
      </div>
    </div>

    <!-- Обычный режим: одно поле + календарь с диапазоном -->
    <template v-else>
      <div
        :class="['custom-date-range__trigger', { 'custom-date-range__trigger--full': fullWidth }]"
        @click.capture="disabled && $event.stopPropagation()"
      >
        <q-input
          :model-value="displayValue"
          :placeholder="placeholder || 'Выберите период'"
          :disable="disabled"
          :required="!!required"
          readonly
          outlined
          dense
          :class="[
            'custom-date-range',
            { 'custom-date-range__full': fullWidth },
            { 'custom-date-range__error_input': showRequiredError }
          ]"
          class="cursor-pointer"
        >
          <template v-slot:append>
            <q-icon
              v-if="value?.from || value?.to"
              name="cancel"
              class="cursor-pointer custom-date-range__icon"
              @click.stop.prevent="clearValue"
            />
            <img v-else src="/img/icons/calendar.svg" class="custom-date-range__calendar-icon" />
          </template>
        </q-input>
        <q-popup-proxy
          ref="popupRef"
          v-model="popupOpen"
          transition-show="scale"
          transition-hide="scale"
          class="custom-date-range-popup-proxy"
        >
          <div class="custom-date-range-popup">
            <div class="custom-date-range-popup__actions">
              <q-btn flat label="Закрыть" color="grey-7" size="sm" no-caps @click="closePopup" />
              <q-btn
                unelevated
                label="Применить"
                color="primary"
                size="sm"
                no-caps
                :disable="!tempModel?.from && !tempModel?.to"
                @click="applySelection"
              />
            </div>
            <q-date
              ref="dateRef"
              v-model="tempModel"
              range
              :options="optionsFn"
              mask="YYYY-MM-DD"
              :locale="dateLocaleRu"
            />
          </div>
        </q-popup-proxy>
      </div>
      <span v-if="showRequiredError" class="custom-date-range__error_message">
        Это поле обязательно
      </span>
    </template>
  </div>
</template>

<script>
import { toValidComponentDate } from "@/_helpers";
import { getLocaleDate } from "@/services";
import { dateLocaleRu } from "@/utils";

export default {
  name: "CustomDateRange",
  props: {
    value: {
      type: Object,
      default: () => ({ from: "", to: "" }),
    },
    placeholder: { type: String, default: "Выберите период" },
    label: { type: String, default: "" },
    required: { type: Boolean, default: false },
    triggerValidationOnMount: { type: Boolean, default: false },
    fullWidth: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    disablePast: { type: Boolean, default: false },
    minDate: { type: [String, Date], default: null },
    maxDate: { type: [String, Date], default: null },
    defaultToToday: { type: Boolean, default: false },
    headerPlaceholder: { type: String, default: "" },
    /** Два отдельных поля «Дата начала» / «Дата окончания», компактный календарь без кнопки «Применить» */
    twoFields: { type: Boolean, default: false },
  },
  emits: ["change_range", "update:value"],
  data() {
    return {
      tempModel: null,
      popupOpen: false,
      popupFromOpen: false,
      popupToOpen: false,
      tempFrom: null,
      tempTo: null,
      fromText: "",
      toText: "",
      dateLocaleRu,
    };
  },
  watch: {
    popupOpen(isOpen) {
      if (isOpen) {
        if (this.disabled) {
          this.$nextTick(() => this.$refs.popupRef?.hide());
        } else {
          this.syncTempFromValue();
          this.$nextTick(() => {
            this.$nextTick(() => this.applyHeaderPlaceholder());
          });
        }
      }
    },
    tempModel() {
      if (this.popupOpen) {
        this.$nextTick(() => this.applyHeaderPlaceholder());
      }
    },
    "value": {
      handler(val) {
        if (this.twoFields) {
          this.fromText = val?.from || "";
          this.toText = val?.to || "";
        }
        if (!val || (!val.from && !val.to)) {
          this.tempModel = null;
          return;
        }
        const from = toValidComponentDate(val.from);
        const to = toValidComponentDate(val.to);
        this.tempModel = from || to ? { from: from || undefined, to: to || undefined } : null;
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    displayValue() {
      const v = this.value;
      if (!v || (!v.from && !v.to)) return "";
      if (v.from && v.to) return `${v.from} — ${v.to}`;
      return v.from || v.to || "";
    },
    showRequiredError() {
      return this.required && !this.value?.from && this.triggerValidationOnMount;
    },
    /** Месяц/год для календаря «Дата окончания»: тот же месяц, что и дата начала (формат YYYY/MM для q-date) */
    defaultYearMonthTo() {
      const comp = toValidComponentDate(this.value?.to) || toValidComponentDate(this.value?.from);
      if (!comp) return undefined;
      const [y, m] = comp.split("-");
      return y && m ? `${y}/${m}` : undefined;
    },
  },
  methods: {
    toValidComponentDate,
    getLocaleDate,
    clearValue() {
      this.$emit("change_range", { from: "", to: "" });
      this.$emit("update:value", { from: "", to: "" });
    },
    clearTo() {
      this.$emit("change_range", { from: this.value?.from || "", to: "" });
      this.$emit("update:value", { from: this.value?.from || "", to: "" });
    },
    /** Останавливаем всплытие клика по полю ввода, чтобы календарь не открывался; клик по иконке календаря не трогаем */
    onInputWrapClick(e) {
      if (e.target.closest?.(".custom-date-range-two__icon-btn")) return;
      e.stopPropagation();
    },
    openFromPopup() {
      if (this.disabled) return;
      this.tempFrom = toValidComponentDate(this.value?.from) || null;
      this.popupFromOpen = true;
    },
    openToPopup() {
      if (this.disabled) return;
      // Показывать календарь в том же месяце, что и дата начала (чтобы не листать с февраля на декабрь)
      const fromComp = toValidComponentDate(this.value?.from);
      const toComp = toValidComponentDate(this.value?.to);
      this.tempTo = toComp || fromComp || null;
      this.$nextTick(() => {
        this.popupToOpen = true;
      });
    },
    onFromPicked(val) {
      if (!val) return;
      const from = getLocaleDate(val);
      this.$emit("change_range", { from, to: this.value?.to || "" });
      this.$emit("update:value", { from, to: this.value?.to || "" });
      this.popupFromOpen = false;
    },
    onToPicked(val) {
      if (!val) return;
      const to = getLocaleDate(val);
      this.$emit("change_range", { from: this.value?.from || "", to });
      this.$emit("update:value", { from: this.value?.from || "", to });
      this.popupToOpen = false;
    },
    /** При вводе цифр автоматически ставит точки: 01102025 → 01.10.2025 (макс. 8 цифр) */
    formatDateInputWithDots(str) {
      if (str == null || str === "") return "";
      const digits = String(str).replace(/\D/g, "").slice(0, 8);
      if (digits.length <= 2) return digits;
      if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
      return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
    },
    /** Парсит введённый текст в дату дд.мм.гггг; принимает 1.12.2025, 01.12.2025 и т.п. */
    parseDateText(str) {
      if (!str || typeof str !== "string") return null;
      const s = str.trim().replace(/\s/g, "");
      const parts = s.split(/[.\-/]/);
      if (parts.length !== 3) return null;
      let [d, m, y] = parts;
      d = d.padStart(2, "0");
      m = m.padStart(2, "0");
      if (y.length === 2) y = "20" + y;
      if (y.length !== 4) return null;
      const day = parseInt(d, 10);
      const month = parseInt(m, 10);
      const year = parseInt(y, 10);
      if (day < 1 || day > 31 || month < 1 || month > 12 || year < 2000 || year > 2100) return null;
      const date = new Date(year, month - 1, day);
      if (date.getDate() !== day || date.getMonth() !== month - 1) return null;
      return `${d}.${m}.${y}`;
    },
    commitFromText() {
      const parsed = this.parseDateText(this.fromText);
      if (parsed) {
        this.$emit("change_range", { from: parsed, to: this.value?.to || "" });
        this.$emit("update:value", { from: parsed, to: this.value?.to || "" });
      } else if (this.fromText.trim() === "") {
        this.$emit("change_range", { from: "", to: this.value?.to || "" });
        this.$emit("update:value", { from: "", to: this.value?.to || "" });
      } else {
        this.fromText = this.value?.from || "";
      }
    },
    commitToText() {
      const parsed = this.parseDateText(this.toText);
      if (parsed) {
        this.$emit("change_range", { from: this.value?.from || "", to: parsed });
        this.$emit("update:value", { from: this.value?.from || "", to: parsed });
      } else if (this.toText.trim() === "") {
        this.$emit("change_range", { from: this.value?.from || "", to: "" });
        this.$emit("update:value", { from: this.value?.from || "", to: "" });
      } else {
        this.toText = this.value?.to || "";
      }
    },
    optionsFnTo(date) {
      if (!this.value?.from) return this.optionsFn(date);
      const fromComp = toValidComponentDate(this.value.from);
      if (!fromComp) return this.optionsFn(date);
      const dateTime = new Date(date).setHours(0, 0, 0, 0);
      const fromTime = new Date(fromComp).setHours(0, 0, 0, 0);
      if (dateTime < fromTime) return false;
      return this.optionsFn(date);
    },
    applySelection() {
      const val = this.tempModel;
      if (!val?.from) return;
      const from = getLocaleDate(val.from);
      const to = val.to ? getLocaleDate(val.to) : "";
      this.$emit("change_range", { from, to });
      this.$emit("update:value", { from, to });
      this.$refs.popupRef?.hide();
    },
    applyHeaderPlaceholder() {
      const el = this.$refs.dateRef?.$el?.querySelector?.(".q-date__header-title");
      if (!el) return;
      const text = el.textContent?.trim() || "";
      const isEmpty = !text || text === "—" || text === "–" || text === "-";
      if (isEmpty) {
        el.textContent = this.headerPlaceholder || this.placeholder || "Выберите период";
      }
    },
    closePopup() {
      this.syncTempFromValue();
      this.$refs.popupRef?.hide();
    },
    syncTempFromValue() {
      const v = this.value;
      if (!v || (!v.from && !v.to)) {
        if (this.defaultToToday) {
          const d = new Date();
          const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          this.tempModel = { from: today, to: today };
        } else {
          this.tempModel = null;
        }
        return;
      }
      const from = toValidComponentDate(v.from);
      const to = toValidComponentDate(v.to);
      this.tempModel = from || to ? { from: from || undefined, to: to || undefined } : null;
    },
    optionsFn(date) {
      const dateTime = new Date(date).setHours(0, 0, 0, 0);

      if (this.minDate) {
        const validMin = toValidComponentDate(this.minDate);
        if (validMin) {
          const minTime = new Date(validMin).setHours(0, 0, 0, 0);
          if (dateTime < minTime) return false;
        }
      }

      if (this.maxDate) {
        const validMax = toValidComponentDate(this.maxDate);
        if (validMax) {
          const maxTime = new Date(validMax).setHours(0, 0, 0, 0);
          if (dateTime > maxTime) return false;
        }
      }

      if (this.disablePast) {
        const today = new Date().setHours(0, 0, 0, 0);
        return dateTime >= today;
      }

      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-date-range-wrapper {
  position: relative;
}

.custom-date-range__trigger {
  display: inline-block;

  &--full {
    width: 100%;
  }
}

.custom-date-range {
  --ci-radius: 8px;

  max-width: 250px;
  min-width: 200px;
  font-size: 12px;

  &__full {
    min-width: 100%;
    width: 100%;
    max-width: initial;
  }

  &__error_input {
    :deep(.q-field__control:before),
    :deep(.q-field__control:after) {
      border-color: #c10015 !important;
      border-style: solid !important;
    }
  }

  &__error_message {
    bottom: -16px;
    font-size: 11px;
    left: 0;
    position: absolute;
    color: #c10015;
  }

  &__icon {
    font-size: 19px;
  }

  &__calendar-icon {
    width: 14px;
    height: 14px;
  }

  &__label {
    font-size: 13px;
    line-height: 13px;
    margin-bottom: 8px;
  }

  :deep(.q-field__control:before) {
    border-radius: var(--ci-radius);
    border-color: #d1d5db !important;
    border-style: solid !important;
  }

  :deep(.q-field__control:after) {
    border-radius: var(--ci-radius) !important;
    border-style: solid !important;
  }

  :deep(.q-field__control-container) {
    min-height: 40px;
  }

  :deep(.q-field__control:hover .q-field__control:before),
  :deep(.q-field__control:focus-within .q-field__control:before) {
    border-color: #1d1d1d !important;
  }

  :deep(.q-placeholder) {
    color: #9ca3af;
    font-size: 12px;
  }

  :deep(.q-field--disabled) {
    .q-field__control {
      background-color: #f5f5f5;
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
}

/* Окно выбора периода: кнопки сверху, не нужно скроллить вниз */
.custom-date-range-popup {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
}

.custom-date-range-popup__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 12px 8px;
  background: #f8f9fa;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.custom-date-range-popup .q-date {
  box-shadow: none;
}

.custom-date-range-popup .q-date__navigation {
  padding: 8px 12px 4px;
}

/* Два поля: компактно, календарь без скролла (minimal) */
.custom-date-range-two {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.custom-date-range-two__field {
  position: relative;
}

.custom-date-range-two__input-wrap {
  position: relative;
}

.custom-date-range-two__input-wrap .custom-date-range-two__input :deep(input) {
  cursor: text;
}

.custom-date-range-two__icon {
  padding: 4px;
  margin: -4px;
  border-radius: 4px;
}

.custom-date-range-two__icon-btn {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  margin: -6px;
  border-radius: 6px;
  user-select: none;
}

.custom-date-range-two__icon-btn:hover {
  background: rgba(0, 0, 0, 0.05);
}

.custom-date-range-two__label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.custom-date-range-two__input {
  width: 100%;
}

.custom-date-range-two__input :deep(.q-field__control) {
  border-radius: 8px;
}

.custom-date-range-two__calendar {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  overflow: hidden;
}

.custom-date-range-two__calendar.q-date {
  max-height: none;
}
</style>
